import { useQuery } from "@tanstack/react-query";
import { postApiWithStatusCode } from "../modules/AmplifyServices";

export const CALENDAR_QUERY_KEY = "calendar-data";

/**
 * Fetches calendar data from the backend hydration endpoint.
 * @param {string} companyId
 * @param {string[]} providerIds
 * @param {string} startDate - ISO 8601
 * @param {string} endDate - ISO 8601
 * @returns {Promise<Object>} { providers, companyTimezone }
 */
const fetchCalendarData = async (
    companyId,
    providerIds,
    startDate,
    endDate
) => {
    const { body, statusCode } = await postApiWithStatusCode(
        "MB-rest-api",
        "/v1/mb/calendar",
        {
            body: {
                companyId,
                providerIds,
                startDate,
                endDate
            }
        }
    );

    if (statusCode >= 400) {
        throw new Error(body?.message || "Failed to fetch calendar data");
    }

    return body;
};

/**
 * React Query hook for fetching calendar data.
 * Caches by provider IDs + date range. Keeps previous data during navigation
 * so the calendar doesn't flash empty when switching weeks.
 *
 * @param {string} companyId - Company ID
 * @param {string[]} providerIds - Array of provider IDs to fetch
 * @param {string} startDate - Start of date range (ISO 8601)
 * @param {string} endDate - End of date range (ISO 8601)
 * @param {Object} options - Additional React Query options
 * @returns {Object} React Query result { data, isLoading, isError, refetch }
 */
export function useCalendarData(
    companyId,
    providerIds,
    startDate,
    endDate,
    options = {}
) {
    const sortedIds = [...(providerIds || [])].sort().join(",");

    return useQuery({
        queryKey: [CALENDAR_QUERY_KEY, companyId, sortedIds, startDate, endDate],
        queryFn: () =>
            fetchCalendarData(companyId, providerIds, startDate, endDate),
        staleTime: 5 * 60 * 1000, // 5 min - calendar data is fresh
        cacheTime: 30 * 60 * 1000, // 30 min cache retention
        keepPreviousData: true, // Smooth week-to-week navigation
        refetchOnWindowFocus: false,
        retry: 2,
        enabled: Boolean(companyId && providerIds?.length && startDate && endDate),
        ...options
    });
}
